import { useRef, useState, type ReactNode } from 'react';
import { HardDrive, Trash2 } from 'lucide-react';
import { Button, Card, ConfirmDialog } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useReclaimRemnant } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import type { SiteDetail, SiteRemnantView } from '@/types/api';

/**
 * Stopped copies a move left behind (ADR-0021).
 *
 * The copy is kept on purpose: until someone has looked at the site on its new
 * server, it is the fastest way back. It also holds disk, which is why it is
 * shown here rather than forgotten, and removed only when someone decides to.
 */
export function RemnantsCard({ site, mayRemove }: { readonly site: SiteDetail; readonly mayRemove: boolean }): ReactNode {
  const [removing, setRemoving] = useState<SiteRemnantView | null>(null);
  // One key per opened confirmation: a double click is the same removal.
  const key = useRef('');
  // Until the job finishes and the page re-reads the site, the copy is still
  // listed; it is shown as under way rather than offered for removal again.
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const reclaim = useReclaimRemnant(
    { siteId: site.id },
    {
      onSuccess: (_result, { remnantId }) => {
        setPending((current) => new Set([...current, remnantId]));
        toast.success('Удаление копии поставлено в очередь');
        setRemoving(null);
      },
    },
  );

  if (site.remnants.length === 0) return null;

  return (
    <Card title="Остановленные копии" icon={HardDrive}>
      <div className="stack stack-tight">
        <p className="field-hint">Копии сайта на прежних серверах после переноса. Удалите их после проверки сайта.</p>
        {site.remnants.map((remnant) => (
          <div key={remnant.id} className="row-between">
            <span>
              Сервер <strong>{remnant.nodeName}</strong>
              {remnant.stopped ? (
                <span className="muted"> · {remnant.reason === 'relocated' ? 'оставлена при восстановлении' : 'остановлена'} {formatRelative(remnant.createdAt)}</span>
              ) : (
                // Left on a server that is gone: stopped when it comes back.
                <span className="tone-degraded"> · сервер недоступен, копия будет остановлена при его возвращении</span>
              )}
            </span>
            {pending.has(remnant.id) ? (
              <span className="muted text-sm">удаляется…</span>
            ) : mayRemove ? (
              <Button
                size="sm"
                variant="danger"
                icon={Trash2}
                onClick={() => {
                  key.current = idempotencyKey('site.reclaim');
                  reclaim.reset();
                  setRemoving(remnant);
                }}
              >
                Удалить копию
              </Button>
            ) : null}
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={removing !== null}
        title="Удалить остановленную копию?"
        description={removing ? `С сервера ${removing.nodeName} будут удалены контейнер, файлы и база этой копии.` : undefined}
        confirmLabel="Удалить копию"
        destructive
        busy={reclaim.isPending}
        error={reclaim.isError ? errorText(reclaim.error) : undefined}
        onConfirm={() => {
          if (removing) reclaim.mutate({ remnantId: removing.id, key: key.current });
        }}
        onClose={() => setRemoving(null)}
      >
        <p className="field-hint">
          Сайт на сервере {site.nodeName ?? '—'} и резервные копии не затрагиваются.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
